import { useIsMutating } from '@tanstack/react-query';
import React, { useEffect, useState, useMemo } from 'react';

import LoadingSvg from '@/components/common/LoadingSvg';
import { Dialog } from '@/components/shopping_bag/Dialog';
import { useAuthenticated } from '@/components/shopping_bag/hooks/useAuthenticated';
import { Button } from '@/components/ui/button';
import { TmCreditCardType, TmCreditCard, TmAddress } from '@/tscapi/model/account';

import { useAddress } from '../Context/AddressContext';
import { useAccount } from '../hooks/useAccount';
import { useRemoveAddress } from '../hooks/useAddress';
import { useRemoveCreditCard } from '../hooks/useCreditcard';

import { DisplayAddressCard } from './DisplayAddressCard';
import { EditAddress } from './EditAddress';

export const ManageShipping = () => {
    const {
        accordionStates,
        setAccordionStates,
        handleAccordionStateChange,
        setShowNotification,
        setShowWarning,
        mode,
        setMode,
        setMode2,
        updateAccordionAtIndex,
    } = useAddress();
    const isMutating = useIsMutating();
    const isAuthenticated = useAuthenticated();
    const { data, isFetching, error } = useAccount(isAuthenticated);
    const mutation = useRemoveAddress();
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [selectedAddressType, setSelectedAddressType] = useState('shipping');
    const [selectedAddress, setSelectedAddress] = useState<TmAddress>(null);
    const [showRemoveDialog, setShowRemoveDialog] = useState(false);
    const shippingAndBillingAddresses = useMemo(() => {
        // Append a billing address to end of shipping address array if shipping is empty
        if (data?.ShippingAddresses.length == 0) {
            return [...data.ShippingAddresses, data.BillingAddress];
        } else {
            return data.ShippingAddresses;
        }
    }, [data.ShippingAddresses, data.BillingAddress]);
    const toggleDialog = () => {
        setShowRemoveDialog(!showRemoveDialog);
    };

    // Custom sorting function
    function customSort(a, b) {
        if (a.IsDefault && !b.IsDefault) {
            return -1; // `a` comes before `b`
        }
        if (!a.IsDefault && b.IsDefault) {
            return 1; // `b` comes before `a`
        }
        return 0; // No change in ordering
    }

    return (
        <>
            {mode == 'view' ? (
                <>
                    {/* view existing addresses */}
                    {data && (
                        <div className="grid grid-cols-1 gap-5 md2:grid-cols-2">
                            {shippingAndBillingAddresses &&
                                shippingAndBillingAddresses.length > 0 &&
                                shippingAndBillingAddresses.sort(customSort).map((card, index) => {
                                    //render shipping address cards
                                    return (
                                        <div className="" key={index}>
                                            <DisplayAddressCard addressData={card} type={'shipping'} />

                                            {data?.BillingAddress.Id !== card.Id ? (
                                                <div className="mt-2.5 grid grid-cols-2 gap-2">
                                                    <Button
                                                        aria-label={'Edit'}
                                                        // className="manage__buttons Edit"
                                                        tabIndex={0}
                                                        size="lg"
                                                        variant="outline"
                                                        onClick={() => {
                                                            updateAccordionAtIndex(0, true);
                                                            updateAccordionAtIndex(1, true);
                                                            updateAccordionAtIndex(2, false);
                                                            setMode('edit');
                                                            setMode2('view');
                                                            setSelectedAddressId(card.Id);
                                                            if (data?.BillingAddress.Id == card.Id) {
                                                                //this is actually the billing address that was placed into the shipping section
                                                                setSelectedAddressType('billing');
                                                            } else {
                                                                setSelectedAddressType('shipping');
                                                            }

                                                            setSelectedAddress(card);
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        aria-label={'Remove'}
                                                        // className="manage__buttons Remove"
                                                        tabIndex={0}
                                                        size="lg"
                                                        variant="outline"
                                                        onClick={() => {
                                                            setSelectedAddressId(card.Id);
                                                            setShowRemoveDialog(true);
                                                        }}
                                                    >
                                                        Remove
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="mt-2.5 grid grid-cols-1 gap-2">
                                                    <Button
                                                        aria-label={'Edit'}
                                                        // className="manage__buttons Edit"
                                                        tabIndex={0}
                                                        size="lg"
                                                        variant="outline"
                                                        onClick={() => {
                                                            updateAccordionAtIndex(0, true);
                                                            updateAccordionAtIndex(1, true);
                                                            updateAccordionAtIndex(2, false);
                                                            setMode('edit');
                                                            setMode2('view');
                                                            setSelectedAddressId(card.Id);
                                                            setSelectedAddressType('billing');
                                                            setSelectedAddress(card);
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </>
            ) : (
                <>
                    {/* edit a specific address */}
                    <EditAddress
                        selectedAddress={selectedAddress}
                        selectedAddressId={selectedAddressId}
                        selectedAddressType={selectedAddressType}
                    />
                </>
            )}
            {showRemoveDialog && (
                <>
                    <Dialog
                        title={'Are you sure you want to remove this address?'}
                        containerCss="removeDialog"
                        height="auto"
                        onCloseClick={() => {
                            setShowRemoveDialog(!showRemoveDialog);
                        }}
                    >
                        <p>
                            All associated information will be removed from your profile except any orders placed with
                            this shipping address.
                        </p>
                        {/* dialog__buttons--container  */}
                        <div
                            className=" mt-4 grid grid-cols-1 gap-5 md:grid-cols-2"
                            key={'dialog-button' + selectedAddressId}
                        >
                            <Button
                                aria-label={'Remove this address'}
                                size="lg"
                                className="md:order-2"
                                // className="dialog__buttons cancel"
                                onClick={() => {
                                    mutation.mutate(selectedAddressId, {
                                        onSuccess: () => {
                                            setShowNotification(false);
                                        },
                                    });
                                    setShowRemoveDialog(!showRemoveDialog);
                                }}
                            >
                                Yes, remove this address
                            </Button>
                            <Button
                                aria-label={'Cancel remove address'}
                                size="lg"
                                variant="outline"
                                className="bg-white"
                                // className="dialog__buttons cancel"
                                onClick={() => {
                                    setShowRemoveDialog(!showRemoveDialog);
                                }}
                            >
                                No, keep this address
                            </Button>
                        </div>
                    </Dialog>
                </>
            )}
            {mutation.isLoading || isMutating ? (
                <div className="blockPageWrap" style={{ display: 'block' }}>
                    <div className="blockPageOverlay"></div>
                    <div className="blockMsg">
                        <LoadingSvg />
                    </div>
                </div>
            ) : null}
        </>
    );
};
